import React, { useState } from 'react';
import { 
  Box, 
  Button, 
  TextField, 
  Typography, 
  Snackbar, 
  Alert,
  Paper,
  CircularProgress,
  Grid,
  FormControlLabel,
  Checkbox,
  useTheme
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { publicInstance } from '../axiosInstance';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import renderBlocks from './renderBlocks';

interface DeviceInfo {
  box: string;
  iccid: string;
  operator?: string;
  time_stamp?: string;
}

interface OperatorCounts {
  Orange: number;
  Play: number;
  Plus: number;
  Unknown: number;
}

// Główny komponent
const BulkShipmentCreation = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [numbers, setNumbers] = useState('');
  const [receiver, setReceiver] = useState('');
  const [date, setDate] = useState<Date | null>(null);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState('');
  const [loading, setLoading] = useState(false);
  const [processedDevices, setProcessedDevices] = useState<DeviceInfo[]>([]);
  const [operatorCounts, setOperatorCounts] = useState<OperatorCounts>({ Orange: 0, Play: 0, Plus: 0, Unknown: 0 });
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'warning' | 'info'>('success');
  const [errors, setErrors] = useState({
    receiver: false,
    date: false,
    numbers: false
  });
  const [isPreviewMode, setIsPreviewMode] = useState(false);

  const determineOperator = (iccid: string): string => {
    if (iccid.startsWith('894803')) return 'Orange';
    if (iccid.startsWith('894806')) return 'Play';
    if (iccid.startsWith('894801')) return 'Plus';
    return 'Unknown';
  };

  const validateForm = () => {
    const newErrors = {
      receiver: !receiver.trim(),
      date: !date,
      numbers: !numbers.trim()
    };
    setErrors(newErrors);
    return !Object.values(newErrors).some(error => error);
  };

  const handleProcess = async () => {
    if (!validateForm()) {
      setSnackbarMessage('Please fill in all required fields');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    try {
      setLoading(true);
      const numberArray = numbers.trim().split(/\s+/).filter(n => n);
      
      if (numberArray.length === 0) {
        setSnackbarMessage('Please enter at least one number');
        setSnackbarSeverity('warning');
        setSnackbarOpen(true);
        return;
      }

      const response = await publicInstance.get(`/dacapi_sensorbox_current?box=in.(${numberArray.join(',')})&select=box,iccid,time_stamp`);
      
      const processedData = response.data.map((device: DeviceInfo) => ({
        ...device,
        operator: determineOperator(device.iccid)
      }));

      // Count operators
      const counts = processedData.reduce((acc: OperatorCounts, device) => {
        const operator = device.operator as keyof OperatorCounts;
        acc[operator]++;
        return acc;
      }, { Orange: 0, Play: 0, Plus: 0, Unknown: 0 });

      setOperatorCounts(counts);
      setProcessedDevices(processedData);
      setIsPreviewMode(true);

    } catch (error) {
      console.error('Error processing devices:', error);
      setSnackbarMessage('Error processing devices');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmShipment = async () => {
    try {
      setLoading(true);
      const username = localStorage.getItem('username');
      const submissionDate = new Date().toISOString();
      
      const devices = [
        { device_type: 'sensorbox(Orange)', quantity: operatorCounts.Orange },
        { device_type: 'sensorbox(Play)', quantity: operatorCounts.Play },
        { device_type: 'sensorbox(Plus)', quantity: operatorCounts.Plus },
        { device_type: 'sensorbox(Unknown)', quantity: operatorCounts.Unknown }
      ].filter(device => device.quantity > 0);

      const submissions = devices.map(device => ({
        submission_date: submissionDate,
        recipient: receiver,
        planned_shipping_date: date ? date.toISOString().split('T')[0] : null,
        comments: comments,
        device_type: device.device_type,
        quantity: device.quantity,
        details: [{}],
        status: 'utworzone',  
        username: username,   
      }));

      const payload = { submissions };

      // 1. Create shipments
      await publicInstance.post('/rpc/insert_multiple_shipments', payload);

      // 2. Update details and status for all devices at once
      const allDetails = processedDevices.map(device => ({
        operator: device.operator,
        box: device.box,
        mac_address: device.box // używamy box jako mac_address
      }));

      await publicInstance.patch(
        `/dacapi_shipment_of_devices?submission_date=eq.${submissionDate}`, 
        { 
          details: allDetails,
          status: 'skompletowane'
        },
        {
          headers: {
            'Prefer': 'return=minimal',
            'Content-Type': 'application/json'
          }
        }
      );

      setSnackbarMessage('Shipment successfully created and completed');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);

      // Clear form
      setNumbers('');
      setReceiver('');
      setDate(null);
      setComments('');
      setProcessedDevices([]);
      setOperatorCounts({ Orange: 0, Play: 0, Plus: 0, Unknown: 0 });
      setIsPreviewMode(false);

    } catch (error) {
      console.error('Error submitting shipment:', error);
      setSnackbarMessage('Error submitting shipment');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') return;
    setSnackbarOpen(false);
  };

  return (
    <Box sx={{ 
      padding: 3,
      backgroundColor: theme.palette.background.default,
      minHeight: '100vh'
    }}>
      {/* Header Paper */}
      <Paper sx={{ 
        p: 3, 
        mb: 3,
        backgroundColor: theme.palette.background.paper,
      }}>
        <Box sx={{ 
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mb: 4
        }}>
          <Button
            variant="contained"
            onClick={() => navigate(-1)}
            sx={{
              minWidth: '100px',
              backgroundColor: theme.palette.background.secondary
            }}
          >
            Back
          </Button>
          <Typography variant="h5">
            Bulk Shipment Creation
          </Typography>
        </Box>

        {/* Main Form Container */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {!isPreviewMode ? (
            <>
              {/* Basic Info Section */}
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  label="Shipment Receiver"
                  value={receiver}
                  onChange={(e) => setReceiver(e.target.value)}
                  error={errors.receiver}
                  helperText={errors.receiver ? "Receiver is required" : ""}
                  sx={{ flex: '1', minWidth: '250px' }}
                />
                
                <Box sx={{ flex: '1', minWidth: '250px' }}>
                  <DatePicker
                    selected={date}
                    onChange={(newDate: Date) => setDate(newDate)}
                    customInput={
                      <TextField
                        label="Planned Shipment Date"
                        fullWidth
                        error={errors.date}
                        helperText={errors.date ? "Date is required" : ""}
                      />
                    }
                    dateFormat="yyyy-MM-dd"
                  />
                </Box>
              </Box>

              {/* Device Numbers Section */}
              <TextField
                label="Device Numbers"
                fullWidth
                multiline
                rows={4}
                value={numbers}
                onChange={(e) => setNumbers(e.target.value)}
                placeholder="Enter device numbers separated by spaces"
                error={errors.numbers}
                helperText={errors.numbers ? "Device numbers are required" : ""}
              />

              {/* Comments Section */}
              <Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showComments}
                      onChange={() => setShowComments(!showComments)}
                      sx={{ 
                        color: theme.palette.text.secondary,
                        '&.Mui-checked': {
                          color: theme.palette.primary.main
                        }
                      }}
                    />
                  }
                  label="Add Comments"
                />
                {showComments && (
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    value={comments}
                    onChange={(e) => setComments(e.target.value)}
                    placeholder="Enter comments"
                    sx={{ mt: 1 }}
                  />
                )}
              </Box>
            </>
          ) : (
            // Preview Mode Summary
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="subtitle1">
                <strong>Receiver:</strong> {receiver}
              </Typography>
              <Typography variant="subtitle1">
                <strong>Planned Date:</strong> {date ? date.toLocaleDateString() : 'Not set'}
              </Typography>
              {comments && (
                <Typography variant="subtitle1">
                  <strong>Comments:</strong> {comments}
                </Typography>
              )}
            </Box>
          )}

          {/* Action Buttons */}
          <Box sx={{ 
            display: 'flex', 
            gap: 2, 
            justifyContent: 'flex-start',
            mt: 2 
          }}>
            {!isPreviewMode ? (
              <Button
                variant="contained"
                onClick={handleProcess}
                disabled={loading}
                startIcon={loading ? 
                  <CircularProgress 
                    size={20} 
                    sx={{ 
                      color: loading ? 'inherit' : undefined 
                    }} 
                  /> : null
                }
                sx={{ 
                  minWidth: '150px',
                  position: 'relative',
                  '&.Mui-disabled': {
                    '& .MuiCircularProgress-root': {
                      color: 'inherit'
                    }
                  }
                }}
              >
                Process Devices
              </Button>
            ) : (
              <>
                <Button
                  variant="outlined"
                  onClick={() => setIsPreviewMode(false)}
                  disabled={loading}
                  sx={{ 
                    minWidth: '120px',
                    '&.Mui-disabled': {
                      backgroundColor: theme.palette.action.disabledBackground,
                      color: theme.palette.action.disabled,
                      opacity: 0.7
                    }
                  }}
                >
                  Edit
                </Button>
                <Button
                  variant="contained"
                  onClick={handleConfirmShipment}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : null}
                  sx={{ 
                    minWidth: '150px',
                    '&.Mui-disabled': {
                      backgroundColor: theme.palette.action.disabledBackground,
                      color: theme.palette.action.disabled,
                      opacity: 0.7
                    }
                  }}
                >
                  Confirm Shipment
                </Button>
              </>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Summary Visualization */}
      {processedDevices.length > 0 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Shipment Summary
          </Typography>
          
          {/* Operator Summary Cards */}
          <Box sx={{ 
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: 2,
            mb: 3
          }}>
            {Object.entries(operatorCounts).map(([operator, count]) => (
              count > 0 && (
                <Box 
                  key={operator}
                  sx={{
                    p: 2,
                    bgcolor: 'background.secondary',
                    borderRadius: 1,
                    textAlign: 'center',
                    border: '1px solid',
                    borderColor: operator === 'Unknown' ? 'grey.500' : 'primary.main'
                  }}
                >
                  <Typography variant="h4" sx={{ mb: 1 }}>
                    {count}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {operator}
                  </Typography>
                </Box>
              )
            ))}
          </Box>

          {/* Devices List */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {Object.entries(operatorCounts).map(([operator, count]) => {
              if (count === 0) return null;
              
              const boxes = processedDevices
                .filter(d => d.operator === operator)
                .map(d => d.box);

              const getOperatorColor = () => {
                switch(operator) {
                  case 'Orange': return '#FF9800';
                  case 'Play': return '#AB47BC';
                  case 'Plus': return '#66BB6A';
                  case 'Unknown': return '#9E9E9E';
                  default: return '#grey';
                }
              };

              return (
                <Box 
                  key={operator} 
                  sx={{ 
                    flex: 1,
                    minWidth: 250,
                    maxWidth: 'calc(50% - 8px)'  // 2 columns with gap
                  }}
                >
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1, 
                    mb: 1 
                  }}>
                    <Box sx={{ 
                      width: 12, 
                      height: 12, 
                      borderRadius: '50%', 
                      bgcolor: getOperatorColor() 
                    }} />
                    <Typography variant="subtitle2">
                      {operator}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ 
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                    gap: 1
                  }}>
                    {boxes.map((box) => {
                      const device = processedDevices.find(d => d.box === box);
                      const timestamp = device?.time_stamp ? new Date(device.time_stamp) : null;
                      
                      // Add check for old timestamp
                      const isOldReading = timestamp && 
                        (new Date().getTime() - timestamp.getTime()) > 24 * 60 * 60 * 1000;
                      
                      return (
                        <Paper
                          key={box}
                          sx={{
                            p: 1,
                            textAlign: 'center',
                            border: `1px solid ${isOldReading ? '#ff4444' : getOperatorColor()}`,
                            bgcolor: isOldReading ? 'rgba(255, 68, 68, 0.1)' : 'background.secondary',
                            fontSize: '0.75rem',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.5,
                            position: 'relative',
                            '&:hover': {
                              bgcolor: isOldReading ? 'rgba(255, 68, 68, 0.2)' : 'background.paper',
                              transform: 'translateY(-2px)',
                              transition: 'transform 0.2s'
                            },
                            '&::after': isOldReading ? {
                              content: '"!"',
                              position: 'absolute',
                              top: -5,
                              right: -5,
                              width: '20px',
                              height: '20px',
                              borderRadius: '50%',
                              backgroundColor: '#ff4444',
                              color: 'white',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '14px',
                              fontWeight: 'bold'
                            } : {}
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {box}
                          </Typography>
                          {timestamp && (
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                color: isOldReading ? '#ff4444' : 'text.secondary',
                                fontSize: '0.65rem',
                                whiteSpace: 'nowrap',
                                fontWeight: isOldReading ? 'bold' : 'normal'
                              }}
                            >
                              {timestamp.toLocaleString()}
                            </Typography>
                          )}
                        </Paper>
                      );
                    })}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Paper>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BulkShipmentCreation; 